import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';
import { NhaContentService } from '../../shared/services/nha-content.service';
import { ToasterService } from '../../services/toaster.service';
import { RolesApiService, SandboxApiService } from '@angular-workspace/services';

export interface PrizeBreakdown {
  rank: string;
  amount: string;
}

export interface ProblemStatement {
  id: number;
  tag: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  domain: string;
  prizes: PrizeBreakdown[];
  /** URL to the full problem statement on the NHA website */
  externalUrl?: string;
}

export interface MasterClass {
  id: number;
  title: string;
  speaker: string;
  designation: string;
  organisation: string;
  date: string;
  time: string;
  mode: string;
  description: string;
  tags: string[];
}

// ── Static fallback data ──────────────────────────────────────────────────────
// Used when the Strapi API is unavailable or returns an empty response.

export const STATIC_PROBLEM_STATEMENTS: ProblemStatement[] = [
  {
    id: 1,
    tag: 'PS-01',
    title: 'Clinical Document Classification & Compliance to STG Requirements',
    description:
      'Automatically classify healthcare documents and perform multilingual OCR on low-quality scans to extract structured, clinical, administrative, and billing data with confidence scores and provenance.',
    difficulty: 'Advanced',
    domain: 'NLP / OCR',
    prizes: [
      { rank: 'Winner 1', amount: '₹ 5,00,000' },
      { rank: 'Winner 2', amount: '₹ 3,00,000' },
      { rank: 'Winner 3', amount: '₹ 2,00,000' },
    ],
    externalUrl: 'https://nha.gov.in/problemStatement1',
  },
  {
    id: 2,
    tag: 'PS-02',
    title: 'Radiological Image-Based Condition Detection & Report Correlation',
    description:
      'Design an assistive solution that can read and interpret medical images to help adjudicators: Understand what the image itself appears to show, Correlate the image findings with the written report submitted by the hospital, and Verify whether both are consistent with the claimed condition, stage, and timeline, as expected under STGs.',
    difficulty: 'Advanced',
    domain: 'Computer Vision / Radiology AI',
    prizes: [
      { rank: 'Winner 1', amount: '₹ 5,00,000' },
      { rank: 'Winner 2', amount: '₹ 3,00,000' },
      { rank: 'Winner 3', amount: '₹ 2,00,000' },
    ],
    externalUrl: 'https://nha.gov.in/problemStatement2',
  },
  {
    id: 3,
    tag: 'PS-03',
    title: 'Document Forgery / Deepfake Detection',
    description:
      'Detect tampering, manipulation, or forgery in submitted documents. Identify altered text, mismatched fonts, inconsistent metadata, detect signature/seal duplications, identify image splicing or erased/overwritten fields.',
    difficulty: 'Advanced',
    domain: 'Forensics / Anomaly Detection',
    prizes: [
      { rank: 'Winner 1', amount: '₹ 5,00,000' },
      { rank: 'Winner 2', amount: '₹ 3,00,000' },
      { rank: 'Winner 3', amount: '₹ 2,00,000' },
    ],
    externalUrl: 'https://nha.gov.in/problemStatement3',
  },
];

const STATIC_MASTER_CLASSES: MasterClass[] = [
  {
    id: 1,
    title: 'Introduction to Auto-Adjudication & AB PMJAY Data Ecosystem',
    speaker: 'Dr. Basant Garg',
    designation: 'CEO',
    organisation: 'National Health Authority',
    date: '8th May 2026',
    time: '09:00 – 10:00 AM',
    mode: 'In-Person',
    description:
      'An overview of the AB PMJAY scheme, the data ecosystem powering it, and the challenges and opportunities in automating claims adjudication at scale.',
    tags: ['Keynote', 'Healthcare', 'Policy'],
  },
  {
    id: 2,
    title: 'Machine Learning Approaches for Claims Processing',
    speaker: 'Prof. Chiranjib Bhattacharyya',
    designation: 'Professor, Dept. of Computer Science',
    organisation: 'Indian Institute of Science (IISc)',
    date: '8th May 2026',
    time: '10:30 – 12:00 PM',
    mode: 'In-Person',
    description:
      'Deep dive into supervised and unsupervised ML techniques applicable to health insurance claims — covering feature engineering, model selection, and evaluation strategies specific to healthcare data.',
    tags: ['Machine Learning', 'Healthcare', 'Technical'],
  },
  {
    id: 3,
    title: 'NLP in Clinical Settings: From Notes to Codes',
    speaker: 'Dr. Sunita Sarawagi',
    designation: 'Professor',
    organisation: 'IIT Bombay',
    date: '8th May 2026',
    time: '02:00 – 03:30 PM',
    mode: 'In-Person',
    description:
      'Practical workshop on applying transformer-based NLP models to clinical text, covering entity recognition, medical concept extraction, and automated ICD coding from unstructured clinical narratives.',
    tags: ['NLP', 'Clinical AI', 'Workshop'],
  },
  {
    id: 4,
    title: 'Responsible AI & Explainability in Healthcare',
    speaker: 'Dr. Pradeep Shenoy',
    designation: 'Research Scientist',
    organisation: 'Google DeepMind India',
    date: '9th May 2026',
    time: '09:30 – 11:00 AM',
    mode: 'In-Person',
    description:
      'Understanding the ethical and regulatory considerations in deploying AI for healthcare decisions, with hands-on examples of SHAP, LIME, and counterfactual explanations for claims adjudication models.',
    tags: ['Responsible AI', 'XAI', 'Ethics'],
  },
  {
    id: 5,
    title: 'Fraud Detection at Scale: Lessons from the Field',
    speaker: 'Shri Deepak Arora',
    designation: 'Director, Analytics',
    organisation: 'National Health Authority',
    date: '9th May 2026',
    time: '11:30 AM – 01:00 PM',
    mode: 'In-Person',
    description:
      'A practitioner session sharing real-world experience in building fraud detection systems for government health schemes — including data challenges, model deployment pipelines, and outcome measurement.',
    tags: ['Fraud Detection', 'Government', 'Practitioner'],
  },
];

@Component({
  selector: 'app-hackathon',
  standalone: true,
  imports: [CommonModule, RouterModule, FooterComponent, LoginModalComponent],
  templateUrl: './hackathon.component.html',
  styleUrl: './hackathon.component.scss',
})
export class HackathonComponent implements OnInit {
  activeSection: 'problems' | 'masterclasses' = 'problems';

  isLoading = true;
  loadError = false;
  showLoginModal = false;
  isSandboxRedirecting = false;
  redirectingSandboxProblemId: number | null = null;

  problemStatements: ProblemStatement[] = [];
  masterClasses: MasterClass[] = [];

  constructor(
    private contentService: NhaContentService,
    private router: Router,
    private keycloak: KeycloakService,
    private rolesService: RolesApiService,
    private sandboxApiService: SandboxApiService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.contentService.fetchHackathonContent().subscribe({
      next: ({ problemStatements, masterClasses }) => {
        this.problemStatements =
          problemStatements.length > 0 ? problemStatements : STATIC_PROBLEM_STATEMENTS;
        this.masterClasses =
          masterClasses.length > 0 ? masterClasses : STATIC_MASTER_CLASSES;
        this.isLoading = false;
      },
      error: () => {
        this.problemStatements = STATIC_PROBLEM_STATEMENTS;
        this.masterClasses = STATIC_MASTER_CLASSES;
        this.isLoading = false;
        this.loadError = true;
      },
    });
  }

  openProblemStatement(ps: ProblemStatement): void {
    this.router.navigate(['/hackathon/problem-statement', ps.id]);
  }

  async openSandbox(ps: ProblemStatement, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.isSandboxDisabled(ps)) {
      return;
    }

    if (this.isSandboxRedirecting) {
      return;
    }

    this.isSandboxRedirecting = true;
    this.redirectingSandboxProblemId = ps.id;

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        this.resetSandboxRedirectState();
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const panelRoute = this.rolesService.getHighestRolePanelRoute(userRoles || []);

      if (panelRoute.startsWith('/admin')) {
        this.toasterService.warning('You dont have access to create the sandbox');
        this.resetSandboxRedirectState();
        return;
      }

      const myProjectsRoute = this.getMyProjectsRouteFromPanel(panelRoute);
      const hasExistingNotebooks = await this.hasExistingNotebooks();
      const tab = hasExistingNotebooks ? 'list' : 'create';

      const navigated = await this.router.navigate([myProjectsRoute], {
        queryParams: { tab, source: 'hackathon', psId: ps.id, psName: ps.title },
      });
      if (!navigated) {
        this.resetSandboxRedirectState();
      }
    } catch (error) {
      console.error('Error opening sandbox from hackathon:', error);
      this.toasterService.warning('Unable to open Sandbox. Please try again.');
      this.resetSandboxRedirectState();
    }
  }

  private resetSandboxRedirectState(): void {
    this.isSandboxRedirecting = false;
    this.redirectingSandboxProblemId = null;
  }

  private getMyProjectsRouteFromPanel(panelRoute: string): string {
    if (panelRoute.startsWith('/org-admin')) {
      return '/org-admin/my-projects';
    }

    if (panelRoute.startsWith('/provider')) {
      return '/provider/my-projects';
    }

    if (panelRoute.startsWith('/admin')) {
      return '/admin/project';
    }

    return '/consumer/my-projects';
  }

  private async hasExistingNotebooks(): Promise<boolean> {
    try {
      const response = await this.sandboxApiService.listNotebooks();
      const notebooks = this.extractNotebookItems(response);
      return notebooks.length > 0;
    } catch (error) {
      console.warn('Unable to fetch notebook list. Falling back to list tab.', error);
      // Avoid false negatives: when in doubt, prefer list tab.
      return true;
    }
  }

  private extractNotebookItems(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (response?.result) {
      if (Array.isArray(response.result)) {
        return response.result;
      }
      if (Array.isArray(response.result.results)) {
        return response.result.results;
      }
      if (Array.isArray(response.result.data)) {
        return response.result.data;
      }
      if (Array.isArray(response.result.notebooks)) {
        return response.result.notebooks;
      }
      if (Array.isArray(response.result.notebooks?.notebooks)) {
        return response.result.notebooks.notebooks;
      }
    }

    if (Array.isArray(response?.notebooks)) {
      return response.notebooks;
    }
    if (Array.isArray(response?.notebooks?.notebooks)) {
      return response.notebooks.notebooks;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    return [];
  }

  difficultyClass(level: string): string {
    return (
      {
        Beginner: 'tag--beginner',
        Intermediate: 'tag--intermediate',
        Advanced: 'tag--advanced',
      }[level] ?? ''
    );
  }

  setSection(section: 'problems' | 'masterclasses'): void {
    this.activeSection = section;
  }

  isSandboxDisabled(ps: ProblemStatement): boolean {
    return ps.id === 2 || ps.id === 3;
  }
}
